import React, { useRef, useState, useEffect } from 'react'
import Typography from '@material-ui/core/Typography'
import Container from '@material-ui/core/Container'
import { getUserContainer } from './container/user-module'
import { ILoggedUserService } from './modules/users/services/LoggedUserService'
import { LOGGED_USER_SERVICE_KEY } from './modules/users'
import { useTranslation } from 'react-i18next'
import background from './assets/login/background.png'
import styles from './Login.module.css'
import logoCareme from './assets/login/logo-careme-login.svg'
import { Box } from '@mui/material'
import { AppButton, ButtonTheme } from './components/app-button/AppButton'
import { toModel } from 'modules/users/models/LoggedUserDTO'
import { LoggedUser } from 'modules/users/models/LoggedUser'
import questionIcon from 'assets/login/question.svg'
import { Dialog, DialogActions, DialogContent, DialogContentText } from '@mui/material'
import { Input } from './loginStylesMui'
import { Header } from './components/header/Header'
import close from './assets/articles/delete.svg'
import { Input as InputDialog } from './features/resource/userProfileStylesMui'
import { GoogleSignInButton } from 'components/google-signin-button/GoogleSignInButton'
import { AppleSignInButton } from 'components/apple-signin-button/AppleSignInButton'
import { STATUS_SERVICE_KEY } from './container/app'
import { IStatusService } from 'common/status/StatusService'

const loggedUserService = getUserContainer().get<ILoggedUserService>(LOGGED_USER_SERVICE_KEY)
const statusService = getUserContainer().get<IStatusService>(STATUS_SERVICE_KEY)

export type LoginProps = {
  onLogin: (user: LoggedUser) => void
  handlePrivacyPolicy: () => void
  handleSetCookies: () => void
}

export default function Login(props: LoginProps) {
  const { t } = useTranslation()
  const [login, setLogin] = useState('')
  const [password, setPassword] = useState('')
  const [hasError, setHasError] = useState(false)
  const [errorType, setErrorType] = useState<
    'none' | 'user_not_found' | 'invalid_password' | 'general'
  >('none')
  const [modalPasswordOpened, setModalPasswordOpened] = useState<boolean>(false)
  const [recoverEmail, setRecoverEmail] = useState<string>('')
  const [showForm, setShowForm] = useState<boolean>(false)
  const formRef = useRef<HTMLDivElement>(null)
  const [showTerms, setShowTerms] = useState(false)
  const termsContainer = useRef<HTMLDivElement>(null)
  const [delayHandler, setDelayHandler] = useState<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    const subscription = statusService.statusObservable().subscribe(async (status: any) => {
      if (status.variant === 'error' && status.error) {
        let resolvedError = status.error

        if (status.error instanceof Promise) {
          try {
            resolvedError = await status.error
          } catch (error) {
            resolvedError = error
          }
        }

        const errorMessage = resolvedError.message || resolvedError.toString()
        let errorType: 'none' | 'user_not_found' | 'invalid_password' | 'general' = 'general'

        if (errorMessage.includes('USER_NOT_FOUND')) {
          errorType = 'user_not_found'
        } else if (errorMessage.includes('INVALID_PASSWORD')) {
          errorType = 'invalid_password'
        } else {
          errorType = 'general'
        }

        setErrorType(errorType)
      }
    })

    return () => subscription.unsubscribe()
  }, [])

  const getErrorMessage = () => {
    switch (errorType) {
      case 'user_not_found':
        return t('userNotFoundError')
      case 'invalid_password':
        return t('invalidPasswordError')
      case 'general':
        return t('authErrorText')
      default:
        return ''
    }
  }

  const handlerLogin = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    doLogin()
  }

  const doLogin = () => {
    setErrorType('none')

    loggedUserService.login({ login, password }).subscribe((res) => {
      if (res) {
        setErrorType('none')
        props.onLogin(toModel(res))
      }
    })
  }

  const handleGoogleSuccess = (response: any) => {
    setErrorType('none')
    setHasError(false)

    if (response && response.id) {
      window.location.href = '/newsWall'
    } else {
      setErrorType('general')
      setHasError(true)
    }
  }

  const handleGoogleError = (error: any) => {
    let newErrorType: 'none' | 'user_not_found' | 'invalid_password' | 'general' = 'general'

    if (error && error.code) {
      switch (error.code) {
        case 'USER_NOT_FOUND':
          newErrorType = 'user_not_found'
          break
        case 'INVALID_PASSWORD':
          newErrorType = 'invalid_password'
          break
        case 'POPUP_BLOCKED':
          newErrorType = 'general'
          break
        case 'EMAIL_ALREADY_EXISTS':
          newErrorType = 'general'
          break
        default:
          newErrorType = 'general'
      }
    }

    setErrorType(newErrorType)
    setHasError(true)
  }

  const handleAppleSuccess = (response: any) => {
    setErrorType('none')
    setHasError(false)

    if (response && response.id) {
      window.location.href = '/newsWall'
    } else {
      setErrorType('general')
      setHasError(true)
    }
  }

  const handleAppleError = (error: any) => {
    let newErrorType: 'none' | 'user_not_found' | 'invalid_password' | 'general' = 'general'

    if (error && error.code) {
      switch (error.code) {
        case 'USER_NOT_FOUND':
          newErrorType = 'user_not_found'
          break
        case 'INVALID_PASSWORD':
          newErrorType = 'invalid_password'
          break
        case 'POPUP_BLOCKED':
          newErrorType = 'general'
          break
        case 'EMAIL_ALREADY_EXISTS':
          newErrorType = 'general'
          break
        default:
          newErrorType = 'general'
      }
    }

    setErrorType(newErrorType)
    setHasError(true)
  }

  const handleSendNewPassword = () => {
    loggedUserService.sendRecoverPassword(recoverEmail)
    setModalPasswordOpened(false)
  }

  const handleClose = () => {
    setModalPasswordOpened(false)
  }

  const handleClick = () => {
    setShowForm(true)
  }

  return (
    <div className={styles.background}>
      <Container component="div" className={styles.mainContainer}>
        <div className={styles.container}>
          {showForm ? (
            <>
              <form className={styles.formContainer} noValidate onSubmit={handlerLogin}>
                <img src={logoCareme} alt="Logo Careme" className={styles.logo} />
                <Box className={styles.inputContainer}>
                  <Input
                    placeholder={t('email')}
                    variant="outlined"
                    margin="normal"
                    required
                    id="login"
                    label=""
                    name="login"
                    autoComplete="login"
                    autoFocus
                    value={login}
                    onChange={(e) => setLogin(e.target.value)}
                  />
                  <Input
                    placeholder={t('password')}
                    variant="outlined"
                    margin="normal"
                    required
                    name="password"
                    label=""
                    type="password"
                    id="password"
                    autoComplete="current-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                  <Typography
                    className={styles.forgottenPasswordText}
                    onClick={() => setModalPasswordOpened(true)}
                  >
                    {t('forgottenPassword')}
                  </Typography>
                  <Box className={styles.button}>
                    <AppButton
                      theme={ButtonTheme.buttonLogin}
                      type={'submit'}
                      handler={doLogin}
                      label={t('enter')}
                      fullWidth={true}
                    />
                  </Box>
                </Box>
              </form>

              <Box className={styles.oauthButtonsContainer}>
                <Box className={styles.oauthButton}>
                  <GoogleSignInButton
                    onSuccess={handleGoogleSuccess}
                    onError={handleGoogleError}
                    label={t('signInWithGoogle')}
                    mode="login"
                  />
                </Box>

                <Box className={styles.oauthButton}>
                  <AppleSignInButton
                    onSuccess={handleAppleSuccess}
                    onError={handleAppleError}
                    label={t('signInWithApple')}
                    mode="login"
                  />
                </Box>
              </Box>

              {errorType !== 'none' && <Box className={styles.errorAlert}>{getErrorMessage()}</Box>}
            </>
          ) : (
            <div ref={formRef} className={styles.formContainerNoShowForm} onClick={handleClick}>
              <img src={logoCareme} alt="Logo Careme" className={styles.logoNoShowForm} />
            </div>
          )}
        </div>
        <Box className={styles.questions}>
          <div
            className={`${styles.termsContainer} ${
              showTerms ? styles.termsContainerVisible : styles.termsContainerHidden
            }`}
            ref={termsContainer}
            onMouseEnter={() => {
              clearTimeout(delayHandler as ReturnType<typeof setTimeout>)
            }}
            onMouseLeave={() => {
              setDelayHandler(
                setTimeout(() => {
                  setShowTerms(false)
                }, 500)
              )
            }}
          >
            <Box className={styles.policyPrivacyContainer}>
              <Typography className={styles.policyPrivacyText} onClick={props.handlePrivacyPolicy}>
                {t('privacy')}
              </Typography>
              <Typography className={styles.policyPrivacyText} onClick={props.handleSetCookies}>
                {t('cookies')}
              </Typography>
            </Box>
          </div>
          <Box
            className={styles.questionContainer}
            onMouseEnter={() => {
              clearTimeout(delayHandler as ReturnType<typeof setTimeout>)
              setShowTerms(true)
            }}
            onMouseLeave={() => {
              setDelayHandler(
                setTimeout(() => {
                  setShowTerms(false)
                }, 500)
              )
            }}
          >
            <img src={questionIcon} alt="logo question" />
          </Box>
        </Box>
      </Container>
      <Dialog
        open={modalPasswordOpened}
        onClose={handleClose}
        PaperProps={{
          className: styles.dialogPaper,
        }}
      >
        <Box className={styles.dialogHeader}>
          <Header label={t('forgotPasswordModalTitle')} icon={close} onClick={handleClose} />
        </Box>

        <DialogContent className={styles.dialogContent}>
          <DialogContentText className={styles.dialogText}>
            {t('forgotPasswordModalText')}
          </DialogContentText>

          <Box className={styles.inputWrapper}>
            <InputDialog
              variant="outlined"
              margin="normal"
              fullWidth
              name="emailRecover"
              placeholder={t('email')}
              label={''}
              id="emailRecover"
              value={recoverEmail}
              onChange={(e) => setRecoverEmail(e.target.value)}
              className={styles.emailInput}
            />
          </Box>
        </DialogContent>

        <DialogActions className={styles.dialogActions}>
          <Box className={styles.buttonsContainer}>
            <AppButton
              theme={ButtonTheme.NewPrimary}
              type={'button'}
              handler={handleSendNewPassword}
              label={t('accept')}
            />
            <AppButton
              theme={ButtonTheme.NewSecondary}
              type={'button'}
              handler={handleClose}
              label={t('cancel')}
            />
          </Box>
        </DialogActions>
      </Dialog>
    </div>
  )
}
